import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Flex, Text, Button, Table, Badge, Dialog, Select, TextField, Heading, AlertDialog, Card, Table as RadixTable } from '@radix-ui/themes';
import { PlusIcon, ReloadIcon, TrashIcon, CameraIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import AdminLayout from './AdminLayout';
import { tellihealthApi, adminApi, smartmeterApi } from '../../services/api';
import { useOrganization } from '../../contexts/OrganizationContext';
import { isValidDeviceId, deviceIdMessage, deviceIdLabel, DEVICE_MANUFACTURERS, SMARTMETER_MANUFACTURER } from '../../utils/imei';
import { formatDateOnly } from '../../utils/formatDateOnly';

interface Device {
  device_id: string;
  organization_id: string | null;
  organization_name: string | null;
  serial_number: string | null;
  device_type: string | null;
  manufacturer: string | null;
  active: boolean;
  gen_ver: string | null;
  patient_id: string | null;
  patient_name: string | null;
  linked_at: string | null;
  // PHA-1124: anchor for this device's 30-day RPM billing cycle.
  //   'smartmeter'          - the vendor's own first-reading date (authoritative)
  //   'smartmeter_readings' - DERIVED: the earliest reading in the vendor's
  //                           history, found by walking back a fixed horizon.
  //                           Only as old as that horizon reached.
  //   'manual'              - an admin override the sweep will never touch again.
  first_data_date: string | null;
  first_data_date_source: 'smartmeter' | 'smartmeter_readings' | 'manual' | null;
}

interface Organization {
  id: string;
  name: string;
}

interface OrphanReading {
  device_id: string;
  reading_count: number;
  first_reading: string | null;
  last_reading: string | null;
  registered: boolean;
  organization_id: string | null;
  organization_name: string | null;
}

interface Reading {
  id: number;
  device_id: string;
  reading_type: string;
  time: string;
  tz_offset_minutes: number | null;
  data: Record<string, any>;
  received_at: string;
}

const AdminDevicesPage: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  // PHA-527: org-state from OrganizationContext.
  const { orgId: ctxOrgId, loading: orgLoading } = useOrganization();
  const sessionOrgId: string | null = ctxOrgId || null;
  const sessionLoaded = !orgLoading;

  const [changingOrgDeviceId, setChangingOrgDeviceId] = useState<string | null>(null);

  // PHA-1124 req 2.2: inline First Data Date editing, one device at a time.
  const [editingFddDeviceId, setEditingFddDeviceId] = useState<string | null>(null);
  const [fddDraft, setFddDraft] = useState('');
  const [savingFdd, setSavingFdd] = useState(false);
  const [syncingFdd, setSyncingFdd] = useState(false);

  // PHA-708: orphaned readings — IMEIs sending data that maps to no patient.
  const [orphans, setOrphans] = useState<OrphanReading[]>([]);
  const [orphansLoading, setOrphansLoading] = useState(true);
  // PHA-1124: 30 days, not 7. AJC's SmartMeter feed started 09/03/2026 and its
  // devices sat unregistered for over a week, so a 7-day window hid exactly the
  // backlog this ticket is about.
  const [orphanWindowDays, setOrphanWindowDays] = useState<number>(30); // 0 = all-time
  const [hiddenUnregisteredCount, setHiddenUnregisteredCount] = useState<number>(0);

  // Form state
  const [formDeviceId, setFormDeviceId] = useState('');
  const [formOrgId, setFormOrgId] = useState<string>('__none__');
  const [formSerial, setFormSerial] = useState('');
  const [formDeviceType, setFormDeviceType] = useState('');
  const [formManufacturer, setFormManufacturer] = useState('Telli-Health');
  const [submitting, setSubmitting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Device | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // Scan with Phone state (PHA-374)
  const [scanMode, setScanMode] = useState(false);
  const [scanToken, setScanToken] = useState<string | null>(null);
  const [scanQrDataUrl, setScanQrDataUrl] = useState<string | null>(null);
  const [scanExpiresAt, setScanExpiresAt] = useState<Date | null>(null);
  const [scanTimeLeft, setScanTimeLeft] = useState('');
  const scanPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scanCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const filterArg = !sessionOrgId || sessionOrgId === 'ALL' ? undefined : sessionOrgId;
      const data = await tellihealthApi.listDevices(filterArg);
      setDevices(data.devices || []);
    } catch {
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [sessionOrgId]);

  const loadOrganizations = useCallback(async () => {
    try {
      const data = await adminApi.getOrganizations();
      const orgs = data.organizations || data.records || data || [];
      setOrganizations(Array.isArray(orgs) ? orgs : []);
    } catch {
      console.error('Failed to load organizations');
    }
  }, []);

  // PHA-708: load orphaned readings, scoped to the selected window + clinic.
  const loadOrphans = useCallback(async () => {
    setOrphansLoading(true);
    try {
      const orgArg = !sessionOrgId || sessionOrgId === 'ALL' ? undefined : sessionOrgId;
      const data = await tellihealthApi.getOrphanReadings(orphanWindowDays, orgArg);
      setOrphans(data.orphans || []);
      setHiddenUnregisteredCount(data.hiddenUnregisteredCount || 0);
    } catch {
      toast.error('Failed to load orphaned readings');
    } finally {
      setOrphansLoading(false);
    }
  }, [sessionOrgId, orphanWindowDays]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    loadOrphans();
  }, [loadOrphans]);

  useEffect(() => {
    if (!sessionLoaded) return;
    loadDevices();
  }, [sessionLoaded, loadDevices]);

  const defaultRegisterOrgId = !sessionOrgId || sessionOrgId === 'ALL' ? '__none__' : sessionOrgId;
  const defaultRegisterOrgIdRef = useRef(defaultRegisterOrgId);
  defaultRegisterOrgIdRef.current = defaultRegisterOrgId;

  useEffect(() => {
    if (!dialogOpen) return;
    setFormOrgId(defaultRegisterOrgIdRef.current);
  }, [dialogOpen]);

  const cleanupScan = useCallback(() => {
    if (scanPollingRef.current) {
      clearInterval(scanPollingRef.current);
      scanPollingRef.current = null;
    }
    if (scanCountdownRef.current) {
      clearInterval(scanCountdownRef.current);
      scanCountdownRef.current = null;
    }
    setScanMode(false);
    setScanToken(null);
    setScanQrDataUrl(null);
    setScanExpiresAt(null);
    setScanTimeLeft('');
  }, []);

  const handleStartScan = async () => {
    try {
      const orgId = formOrgId === '__none__' ? undefined : formOrgId;
      const data = await tellihealthApi.createScanToken(orgId);
      const token = data.token;
      const expiresAt = new Date(data.expiresAt);

      setScanToken(token);
      setScanExpiresAt(expiresAt);
      setScanMode(true);

      // Generate QR code URL
      const scanUrl = `${window.location.origin}/scan/${token}`;
      const dataUrl = await QRCode.toDataURL(scanUrl, { width: 256, margin: 2 });
      setScanQrDataUrl(dataUrl);

      // Start countdown timer
      scanCountdownRef.current = setInterval(() => {
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000));
        const mins = Math.floor(remaining / 60);
        const secs = remaining % 60;
        setScanTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
        if (remaining <= 0) {
          toast.error('Scan session expired');
          cleanupScan();
        }
      }, 1000);

      // Start polling for results
      scanPollingRef.current = setInterval(async () => {
        try {
          const result = await tellihealthApi.getScanTokenResult(token);
          if (result.status === 'scanned') {
            setFormSerial(result.serialNumber || '');
            // PHA-1103: validate the scanned id against the manufacturer already
            // selected in the form, or a scanned SmartMeter id is rejected here
            // as a bad IMEI before it can ever be registered.
            if (result.imei && !isValidDeviceId(result.imei, formManufacturer)) {
              toast.error(deviceIdMessage(formManufacturer));
              cleanupScan();
            } else {
              setFormDeviceId(result.imei || '');
              toast.success('Device info received from phone scan!');
              cleanupScan();
            }
          } else if (result.status === 'expired') {
            toast.error('Scan session expired');
            cleanupScan();
          }
        } catch {
          // Silently retry on next interval
        }
      }, 3000);
    } catch {
      toast.error('Failed to start scan session');
    }
  };

  // Cleanup scan on dialog close
  useEffect(() => {
    if (!dialogOpen) {
      cleanupScan();
    }
  }, [dialogOpen, cleanupScan]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scanPollingRef.current) clearInterval(scanPollingRef.current);
      if (scanCountdownRef.current) clearInterval(scanCountdownRef.current);
    };
  }, []);

  const handleRegister = async () => {
    setRegisterError(null);
    setShowErrorAlert(false);
    if (!formDeviceId.trim()) {
      setRegisterError(`${deviceIdLabel(formManufacturer)} is required`);
      return;
    }
    // PHA-1103: validate by manufacturer. SmartMeter glucose meters have 7-digit
    // ids, which isValidImei rejects on length in every environment.
    if (!isValidDeviceId(formDeviceId.trim(), formManufacturer)) {
      setRegisterError(deviceIdMessage(formManufacturer));
      setShowErrorAlert(true);
      return;
    }
    setSubmitting(true);
    try {
      const result = await tellihealthApi.registerDevice(formDeviceId.trim(), formOrgId === '__none__' ? undefined : formOrgId, formSerial.trim() || undefined, formDeviceType.trim() || undefined, formManufacturer.trim() || undefined);
      toast.success(`Device ${formDeviceId} registered`);
      // PHA-708: warn immediately if this IMEI has never transmitted any data —
      // the early signal that a wrong/mistyped number was entered.
      const stats = result?.readingStats;
      if (stats && stats.count === 0) {
        toast(`No readings on file yet for device ${formDeviceId}. If this is a replacement, verify the number matches the unit; for a new device, take a test reading and confirm it appears.`, { icon: '⚠️', duration: 8000 });
      } else if (stats && stats.count > 0) {
        toast.success(`${stats.count} reading(s) already on file for this device.`);
      }
      setDialogOpen(false);
      setFormDeviceId('');
      setFormOrgId(defaultRegisterOrgId);
      setFormSerial('');
      setFormDeviceType('');
      setFormManufacturer('Telli-Health');
      loadDevices();
      loadOrphans(); // registering the true IMEI may clear an orphan
    } catch (err: any) {
      const body = err?.errorData || {};
      const errorMsg = body.detail
        ? `${body.error || 'Failed to register device'}: ${body.detail}`
        : body.error || 'Failed to register device';
      setRegisterError(errorMsg);
      setShowErrorAlert(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (device: Device) => {
    try {
      await tellihealthApi.updateDeviceStatus(device.device_id, !device.active);
      toast.success(`Device ${device.device_id} ${!device.active ? 'activated' : 'deactivated'}`);
      loadDevices();
    } catch {
      toast.error('Failed to update device status');
    }
  };

  // PHA-1124 req 2.2: write the admin's First Data Date. Saving marks the device
  // 'manual', which permanently exempts it from the SmartMeter refresh sweep —
  // otherwise the next sweep (every 30 minutes) would restore the vendor's value
  // and the override would look like it silently failed.
  const handleSaveFirstDataDate = async (deviceId: string) => {
    setSavingFdd(true);
    try {
      await tellihealthApi.setDeviceFirstDataDate(deviceId, fddDraft || null);
      toast.success(
        fddDraft
          ? `First Data Date set to ${fddDraft} — this device's 30-day RPM cycles now start there`
          : 'First Data Date cleared — cycles fall back to the earliest reading',
      );
      setEditingFddDeviceId(null);
      loadDevices();
    } catch {
      toast.error('Failed to set First Data Date');
    } finally {
      setSavingFdd(false);
    }
  };

  // PHA-1124 req 2.1: pull "First Reading" from SmartMeter for every device that
  // has no anchor yet. Skips admin overrides and non-SmartMeter devices.
  const handleSyncFirstDataDates = async () => {
    setSyncingFdd(true);
    try {
      const result = await smartmeterApi.syncFirstDataDates({});
      if (result?.error) {
        toast.error(result.error);
      } else {
        toast.success(
          `First Data Dates: ${result.updated} updated, ${result.unchanged} unchanged` +
          (result.missing ? `, ${result.missing} not reported by the vendor` : '') +
          (result.failed ? `, ${result.failed} failed` : ''),
        );
        loadDevices();
      }
    } catch {
      toast.error('Failed to sync First Data Dates from SmartMeter');
    } finally {
      setSyncingFdd(false);
    }
  };

  const handleRefreshGen = async (deviceId: string) => {
    try {
      const data = await tellihealthApi.getDeviceGen(deviceId);
      toast.success(`Device gen: ${data.genVer}`);
      loadDevices();
    } catch {
      toast.error('Failed to fetch device generation');
    }
  };

  const handleRemoveDevice = async () => {
    if (!removeTarget) return;
    try {
      await tellihealthApi.removeDevice(removeTarget.device_id);
      toast.success(`Device ${removeTarget.device_id} removed`);
      setRemoveTarget(null);
      loadDevices();
    } catch (err: any) {
      const body = err?.errorData || {};
      toast.error(body.error || 'Failed to remove device');
    }
  };

  const handleOrgChange = async (deviceId: string, newOrgId: string) => {
    setChangingOrgDeviceId(deviceId);
    try {
      await tellihealthApi.updateDeviceOrganization(deviceId, newOrgId);
      toast.success('Device organization updated');
      loadDevices();
    } catch (err: any) {
      const body = err?.errorData || {};
      toast.error(body.error || 'Failed to update device organization');
    } finally {
      setChangingOrgDeviceId(null);
    }
  };

  // PHA-375: Device readings state and handlers
  const [expansionState, setExpansionState] = useState<Record<string, boolean>>({});
  const [deviceReadings, setDeviceReadings] = useState<Record<string, Reading[]>>({});
  const [readingsTotal, setReadingsTotal] = useState<Record<string, number>>({});
  const [readingsLoading, setReadingsLoading] = useState<Record<string, boolean>>({});
  const [readingsFromDate, setReadingsFromDate] = useState<string>('');
  const [readingsToDate, setReadingsToDate] = useState<string>('');
  const [readingsType, setReadingsType] = useState<string>('all');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const fetchReadingsForDevice = useCallback(async (deviceId: string) => {
    setReadingsLoading(prev => ({ ...prev, [deviceId]: true }));
    try {
      const readingTypeFilter = readingsType === 'all' ? undefined : readingsType;
      const result = await tellihealthApi.getDeviceReadings(deviceId, {
        fromDate: readingsFromDate || undefined,
        toDate: readingsToDate || undefined,
        readingType: readingTypeFilter,
        limit: 200,
      });
      const readings = result.readings || [];
      setDeviceReadings(prev => ({ ...prev, [deviceId]: readings }));
      setReadingsTotal(prev => ({ ...prev, [deviceId]: result.total ?? readings.length }));
    } catch {
      toast.error('Failed to load device readings');
      setDeviceReadings(prev => ({ ...prev, [deviceId]: [] }));
      setReadingsTotal(prev => ({ ...prev, [deviceId]: 0 }));
    } finally {
      setReadingsLoading(prev => ({ ...prev, [deviceId]: false }));
    }
  }, [readingsFromDate, readingsToDate, readingsType]);

  const handleToggleReadings = (device: Device) => {
    const willOpen = !expansionState[device.device_id];
    setExpansionState(prev => ({ ...prev, [device.device_id]: willOpen }));
    if (willOpen && !deviceReadings[device.device_id]) {
      fetchReadingsForDevice(device.device_id);
    }
  };

  // Reload readings for any open panel when filters change
  useEffect(() => {
    Object.keys(expansionState).forEach(deviceId => {
      if (expansionState[deviceId]) {
        fetchReadingsForDevice(deviceId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readingsFromDate, readingsToDate, readingsType]);

  // PHA-375: Sort handler
  const handleSort = (key: string) => {
    const direction = sortConfig && sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc';
    setSortConfig({ key, direction });
  };

  const getSortedReadings = (readings: Reading[]): Reading[] => {
    if (!sortConfig) return readings;
    return [...readings].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof Reading];
      const bValue = b[sortConfig.key as keyof Reading];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const formatReadingData = (type: string, data: Record<string, any>): string => {
    switch (type) {
      case 'BloodPressure':
        return `${data.systolic}/${data.diastolic} mmHg, pulse ${data.pulse}${data.arrhythmia ? ' (ARR)' : ''}`;
      case 'BloodGlucose':
        return `${data.glucose} ${data.unit || 'mg/dL'} (${data.readingPeriod || ''})`;
      case 'Weight':
        return `${data.weight} ${data.unit || 'lb'}`;
      case 'SpO2':
        return `SpO2 ${data.spo2}%, pulse ${data.pulse}`;
      case 'Temperature':
        return `${data.temperature} ${data.unit || 'F'}`;
      case 'PeakFlow':
        return `FEV1 ${data.fev1}L, PEF ${data.pef}`;
      default:
        return JSON.stringify(data);
    }
  };

  const formatDate = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleString() : '—';

  return (
    <AdminLayout title="Devices" subtitle="Manage RPM devices">
      <Flex justify="between" align="center" mb="4">
        <Flex gap="3" align="center">
          <Button variant="soft" size="2" onClick={loadDevices} disabled={loading}>
            <ReloadIcon /> Refresh
          </Button>
          {/* PHA-1124 req 2.1: pull each SmartMeter device's "First Reading" date
              and store it as the cycle anchor. Also runs on the 30-minute sweep;
              this is the "do it now" after registering a batch. */}
          <Button
            variant="soft"
            size="2"
            onClick={handleSyncFirstDataDates}
            disabled={syncingFdd}
            title="Pull First Data Dates from SmartMeter for devices that have none"
          >
            <ReloadIcon /> {syncingFdd ? 'Syncing…' : 'Sync First Data Dates'}
          </Button>
        </Flex>

        <Dialog.Root open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) { setRegisterError(null); setShowErrorAlert(false); }
        }}>
          <Dialog.Trigger>
            <Button><PlusIcon /> Register Device</Button>
          </Dialog.Trigger>
          <Dialog.Content style={{ maxWidth: 450 }}>
            <Dialog.Title>Register New Device</Dialog.Title>
            <Dialog.Description size="2" mb="4">
              Enter the device IMEI and details. Device generation will be fetched automatically and alerts will be disabled.
            </Dialog.Description>

            {/* Scan with Phone panel (PHA-374) */}
            {scanMode && scanQrDataUrl ? (
              <Box mb="3" p="3" style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                <Text size="2" weight="medium" mb="2" as="div">
                  Scan this QR code with your phone
                </Text>
                <img src={scanQrDataUrl} alt="Scan QR code" style={{ width: 200, height: 200, margin: '8px auto' }} />
                <Text size="1" color="gray" as="div" mb="2">
                  Time remaining: {scanTimeLeft}
                </Text>
                <Button variant="soft" size="1" color="gray" onClick={cleanupScan}>
                  Cancel Scan
                </Button>
              </Box>
            ) : (
              <Box mb="3">
                <Button variant="outline" size="2" onClick={handleStartScan} style={{ width: '100%' }}>
                  <CameraIcon /> Scan with Phone
                </Button>
              </Box>
            )}

            <Flex direction="column" gap="3">
              {/* PHA-1103: manufacturer first — it decides how the id below is
                  validated and labelled, so asking for the id first would let an
                  operator type a valid SmartMeter id into a field still
                  rejecting it as a bad IMEI. */}
              <label>
                <Text size="2" weight="bold" mb="1" as="div">Manufacturer</Text>
                <Select.Root value={formManufacturer} onValueChange={setFormManufacturer}>
                  <Select.Trigger placeholder="Select manufacturer" />
                  <Select.Content>
                    {DEVICE_MANUFACTURERS.map((m) => (
                      <Select.Item key={m} value={m}>{m}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </label>
              <label>
                <Text size="2" weight="bold" mb="1" as="div">{deviceIdLabel(formManufacturer)} *</Text>
                <TextField.Root>
                  <TextField.Input
                    value={formDeviceId}
                    onChange={(e) => setFormDeviceId(e.currentTarget.value)}
                    placeholder={formManufacturer === SMARTMETER_MANUFACTURER ? '1234567' : '864593054464085'}
                  />
                </TextField.Root>
                {formManufacturer === SMARTMETER_MANUFACTURER && (
                  <Text size="1" color="gray" as="div" mt="1">
                    SmartMeter joins readings on the device id, not the IMEI — 15 digits for a blood pressure cuff, 7 for a glucose meter.
                  </Text>
                )}
              </label>
              <label>
                <Text size="2" weight="bold" mb="1" as="div">Organization</Text>
                <Select.Root value={formOrgId} onValueChange={setFormOrgId}>
                  <Select.Trigger placeholder="Select organization" />
                  <Select.Content>
                    <Select.Item value="__none__">None</Select.Item>
                    {organizations.map((org) => (
                      <Select.Item key={org.id} value={org.id}>{org.name}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </label>
              <label>
                <Text size="2" weight="bold" mb="1" as="div">Serial Number</Text>
                <TextField.Root>
                  <TextField.Input
                    value={formSerial}
                    onChange={(e) => setFormSerial(e.currentTarget.value)}
                    placeholder="From vendor purchase email"
                  />
                </TextField.Root>
              </label>
              <label>
                <Text size="2" weight="bold" mb="1" as="div">Device Type</Text>
                <TextField.Root>
                  <TextField.Input
                    value={formDeviceType}
                    onChange={(e) => setFormDeviceType(e.currentTarget.value)}
                    placeholder="e.g. Blood Pressure Cuff"
                  />
                </TextField.Root>
              </label>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button onClick={handleRegister} disabled={submitting}>
                {submitting ? 'Registering...' : 'Register'}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </Flex>

      {/* PHA-375: Device Readings Filter Controls */}
      <Card variant="surface" mb="3">
        <Flex align="center" justify="between" gap="3">
          <Flex gap="3" align="center">
            <Text size="2" weight="bold">Readings Filters:</Text>
            <TextField.Root>
              <TextField.Input
                type="date"
                value={readingsFromDate}
                onChange={(e) => setReadingsFromDate(e.target.value)}
                placeholder="From date"
                style={{ width: '150px' }}
              />
            </TextField.Root>
            <TextField.Root>
              <TextField.Input
                type="date"
                value={readingsToDate}
                onChange={(e) => setReadingsToDate(e.target.value)}
                placeholder="To date"
                style={{ width: '150px' }}
              />
            </TextField.Root>
            <Select.Root value={readingsType} onValueChange={(v) => setReadingsType(v)}>
              <Select.Trigger style={{ width: '150px' }} placeholder="All types"
              />
              <Select.Content>
                <Select.Item value="all">All Types</Select.Item>
                <Select.Item value="BloodPressure">Blood Pressure</Select.Item>
                <Select.Item value="BloodGlucose">Blood Glucose</Select.Item>
                <Select.Item value="Weight">Weight</Select.Item>
                <Select.Item value="SpO2">SpO2</Select.Item>
                <Select.Item value="Temperature">Temperature</Select.Item>
                <Select.Item value="PeakFlow">Peak Flow</Select.Item>
              </Select.Content>
            </Select.Root>
          </Flex>
        </Flex>
      </Card>

      {/* PHA-708: orphaned readings — devices sending data that maps to no patient.
          Always rendered so the Window selector stays reachable even when the
          current window has 0 orphans (otherwise the whole panel disappears). */}
      <Card variant="surface" mb="3" style={{ borderColor: orphans.length > 0 ? '#f59e0b' : undefined }}>
          <Flex align="center" justify="between" mb="2">
            <Flex align="center" gap="2">
              <Text size="2" weight="bold">⚠️ Orphaned readings — data with no patient</Text>
              {!orphansLoading && (
                <Badge color={orphans.length > 0 ? 'orange' : 'green'} variant="soft">
                  {orphans.length}
                </Badge>
              )}
            </Flex>
            <Flex align="center" gap="2">
              <Text size="1" color="gray">Window:</Text>
              <Select.Root value={String(orphanWindowDays)} onValueChange={(v) => setOrphanWindowDays(Number(v))}>
                <Select.Trigger variant="soft" />
                <Select.Content>
                  <Select.Item value="7">Last 7 days</Select.Item>
                  <Select.Item value="30">Last 30 days</Select.Item>
                  <Select.Item value="90">Last 90 days</Select.Item>
                  <Select.Item value="0">All time</Select.Item>
                </Select.Content>
              </Select.Root>
              <Button variant="soft" size="1" onClick={loadOrphans} disabled={orphansLoading}>
                <ReloadIcon /> Refresh
              </Button>
            </Flex>
          </Flex>
          <Text size="1" color="gray">
            These IMEIs are transmitting readings but are linked to no patient — the readings are
            invisible on every chart. Usually the IMEI registered/linked differs from the number the
            device actually broadcasts. Register the true IMEI and link it to the correct patient
            (confirm by serial number) to recover the data.
          </Text>
          {hiddenUnregisteredCount > 0 && (
            <Text size="1" color="orange" mt="1" as="p">
              + {hiddenUnregisteredCount} unregistered orphan{hiddenUnregisteredCount === 1 ? '' : 's'} not
              shown — these have no clinic. Select <strong>All clinics</strong> to view them.
            </Text>
          )}
          {orphansLoading ? (
            <Text size="2" color="gray">Loading...</Text>
          ) : orphans.length === 0 ? (
            <Text size="2" color="gray" mt="2" as="p">No orphaned readings in the selected window.</Text>
          ) : (
            <Table.Root variant="surface" mt="2">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>IMEI (transmitting)</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Readings</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>First</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Last</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Registered?</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Org</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {orphans.map((o) => (
                  <Table.Row key={o.device_id}>
                    <Table.Cell>
                      <Text size="2" style={{ fontFamily: 'monospace' }}>{o.device_id}</Text>
                    </Table.Cell>
                    <Table.Cell><Text size="2">{o.reading_count}</Text></Table.Cell>
                    <Table.Cell>
                      <Text size="2">{o.first_reading ? new Date(o.first_reading).toLocaleDateString() : '—'}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2">{o.last_reading ? new Date(o.last_reading).toLocaleString() : '—'}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={o.registered ? 'blue' : 'gray'} variant="soft">
                        {o.registered ? 'Registered, unlinked' : 'Not registered'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell><Text size="2">{o.organization_name || '—'}</Text></Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </Card>

      <Table.Root variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>IMEI</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Organization</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Serial</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Device Type</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Manufacturer</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Gen</Table.ColumnHeaderCell>
            {/* PHA-1124: the anchor for this device's 30-day RPM billing cycle. */}
            <Table.ColumnHeaderCell>First Data Date</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Linked Patient</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {loading && (
            <Table.Row>
              <Table.Cell colSpan={10}><Text size="2" color="gray">Loading...</Text></Table.Cell>
            </Table.Row>
          )}
          {!loading && devices.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={10}><Text size="2" color="gray">No devices registered</Text></Table.Cell>
            </Table.Row>
          )}
          {devices.map((device) => [
            <Table.Row key={device.device_id}>
              <Table.Cell>
                <Text size="2" style={{ fontFamily: 'monospace' }}>{device.device_id}</Text>
              </Table.Cell>
              <Table.Cell>
                {device.patient_id ? (
                  <Text size="2">{device.organization_name || '-'}</Text>
                ) : (
                  <Select.Root
                    value={device.organization_id ?? undefined}
                    onValueChange={(val) => handleOrgChange(device.device_id, val)}
                    disabled={changingOrgDeviceId === device.device_id}
                  >
                    <Select.Trigger variant="ghost" />
                    <Select.Content>
                      {organizations.map((org) => (
                        <Select.Item key={org.id} value={org.id}>{org.name}</Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                )}
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{device.serial_number || '-'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{device.device_type || '\u2014'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{device.manufacturer || '\u2014'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Flex align="center" gap="1">
                  <Text size="2">{device.gen_ver || '-'}</Text>
                  <Button variant="ghost" size="1" onClick={() => handleRefreshGen(device.device_id)} title="Refresh from API">
                    <ReloadIcon />
                  </Button>
                </Flex>
              </Table.Cell>
              <Table.Cell>
                {editingFddDeviceId === device.device_id ? (
                  <Flex align="center" gap="1">
                    <TextField.Root>
                      <TextField.Input
                        type="date"
                        size="1"
                        value={fddDraft}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setFddDraft(e.target.value)}
                        style={{ width: '150px' }}
                      />
                    </TextField.Root>
                    <Button size="1" variant="soft" disabled={savingFdd} onClick={() => handleSaveFirstDataDate(device.device_id)}>
                      Save
                    </Button>
                    <Button size="1" variant="ghost" color="gray" disabled={savingFdd} onClick={() => setEditingFddDeviceId(null)}>
                      Cancel
                    </Button>
                  </Flex>
                ) : (
                  <Flex align="center" gap="1">
                    <Text size="2">
                      {formatDateOnly(device.first_data_date)}
                    </Text>
                    {device.first_data_date_source === 'manual' && (
                      <Badge color="amber" variant="soft" title="Set by an admin; the SmartMeter sync will not overwrite it">
                        manual
                      </Badge>
                    )}
                    {/* A billing-cycle anchor is the kind of number someone asks
                        the provenance of a year later, so "the vendor told us"
                        and "we inferred it" must not render identically. */}
                    {device.first_data_date_source === 'smartmeter_readings' && (
                      <Badge color="gray" variant="soft" title="Derived: the earliest reading in SmartMeter's history for this device, not their own first-reading field (which our plan cannot read). Only as old as the search horizon reached.">
                        derived
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="1"
                      title="Set the 30-day RPM cycle anchor for this device"
                      onClick={() => {
                        setEditingFddDeviceId(device.device_id);
                        setFddDraft(device.first_data_date ? String(device.first_data_date).slice(0, 10) : '');
                      }}
                    >
                      Edit
                    </Button>
                  </Flex>
                )}
              </Table.Cell>
              <Table.Cell>
                <Badge color={device.active ? 'green' : 'red'} variant="soft">
                  {device.active ? 'Active' : 'Inactive'}
                </Badge>
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{device.patient_name || device.patient_id || '-'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Flex gap="2">
                  <Button
                    variant="soft"
                    size="1"
                    color={device.active ? 'red' : 'green'}
                    onClick={() => handleToggleActive(device)}
                  >
                    {device.active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="soft"
                    size="1"
                    color="indigo"
                    onClick={() => handleToggleReadings(device)}
                    title="View device readings"
                  >
                    View Readings
                  </Button>
                  {!device.patient_id && (
                    <Button variant="soft" size="1" color="red" onClick={() => setRemoveTarget(device)}>
                      <TrashIcon /> Remove
                    </Button>
                  )}
                </Flex>
              </Table.Cell>
            </Table.Row>,
            ...(expansionState[device.device_id]
              ? [
                  <Table.Row key={`${device.device_id}-readings`}>
                    <Table.Cell colSpan={10}>
                      <Card variant="surface" mt="2" style={{ padding: '12px', maxHeight: 600, overflow: 'auto' }}>
                    <Flex justify="between" align="center" mb="2">
                      <Flex align="center" gap="2">
                            <Text size="2" weight="bold">Readings for {device.device_id}</Text>
                            <Text size="2" color="gray">({readingsTotal[device.device_id] || 0} total)</Text>
                      </Flex>
                      {readingsLoading[device.device_id] && (
                        <Text size="2" color="gray">Loading...</Text>
                      )}
                    </Flex>
                    {readingsLoading[device.device_id] ? (
                      <Flex justify="center" align="center" py="4">
                        <Text size="2" color="gray">Loading readings...</Text>
                      </Flex>
                    ) : deviceReadings[device.device_id] && deviceReadings[device.device_id].length > 0 ? (
                      <RadixTable.Root variant="surface">
                        <RadixTable.Header>
                          <RadixTable.Row>
                            <RadixTable.ColumnHeaderCell
                              onClick={() => handleSort('time')}
                              style={{ cursor: 'pointer' }}
                            >
                              Date/Time
                              {sortConfig?.key === 'time' && (
                                <Text size="1" ml="1">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </Text>
                              )}
                            </RadixTable.ColumnHeaderCell>
                            <RadixTable.ColumnHeaderCell
                              onClick={() => handleSort('reading_type')}
                              style={{ cursor: 'pointer' }}
                            >
                              Reading Type
                              {sortConfig?.key === 'reading_type' && (
                                <Text size="1" ml="1">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </Text>
                              )}
                            </RadixTable.ColumnHeaderCell>
                            <RadixTable.ColumnHeaderCell>Data</RadixTable.ColumnHeaderCell>
                            <RadixTable.ColumnHeaderCell
                              onClick={() => handleSort('received_at')}
                              style={{ cursor: 'pointer' }}
                            >
                              Received
                              {sortConfig?.key === 'received_at' && (
                                <Text size="1" ml="1">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </Text>
                              )}
                            </RadixTable.ColumnHeaderCell>
                          </RadixTable.Row>
                        </RadixTable.Header>
                        <RadixTable.Body>
                          {getSortedReadings(deviceReadings[device.device_id]).map((reading) => (
                            <RadixTable.Row key={reading.id}>
                              <RadixTable.Cell>
                                <Text size="2">
                                  {new Date(reading.time).toLocaleString()}
                                </Text>
                              </RadixTable.Cell>
                              <RadixTable.Cell>
                                <Text size="2">
                                  <Badge variant="soft">{reading.reading_type}</Badge>
                                </Text>
                              </RadixTable.Cell>
                              <RadixTable.Cell>
                                <Text size="2" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                  {formatReadingData(reading.reading_type, reading.data)}
                                </Text>
                              </RadixTable.Cell>
                              <RadixTable.Cell>
                                <Text size="2" color="gray">
                                  {formatDate(reading.received_at)}
                                </Text>
                              </RadixTable.Cell>
                            </RadixTable.Row>
                          ))}
                        </RadixTable.Body>
                      </RadixTable.Root>
                    ) : (
                      <Flex justify="center" align="center" py="4">
                        <Text size="2" color="gray">No readings found for this device</Text>
                      </Flex>
                    )}
                  </Card>
                </Table.Cell>
              </Table.Row>
              ]
              : [])])}
        </Table.Body>
      </Table.Root>

      <AlertDialog.Root open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialog.Content style={{ maxWidth: 460 }}>
          <AlertDialog.Title>Remove Device</AlertDialog.Title>
          <AlertDialog.Description size="2" color="gray">
            Remove device {removeTarget?.device_id}? This action cannot be undone.
          </AlertDialog.Description>
          <Flex justify="end" gap="2" mt="4">
            <AlertDialog.Cancel>
              <Button variant="soft">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={handleRemoveDevice}>
                Remove
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <AlertDialog.Root open={showErrorAlert} onOpenChange={(open) => { if (!open) setShowErrorAlert(false); }}>
        <AlertDialog.Content style={{ maxWidth: 420 }}>
          <AlertDialog.Title>Registration Failed</AlertDialog.Title>
          <AlertDialog.Description size="2">
            {registerError}
          </AlertDialog.Description>
          <Flex justify="end" mt="4">
            <AlertDialog.Action>
              <Button variant="solid" color="red">OK</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </AdminLayout>
  );
};

export default AdminDevicesPage;
